import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useI18n } from 'core/i18n/I18nProvider';
import { paths } from 'core/routing/paths';
import { canRequestConsultation, isActiveConsultationStatus } from 'features/consultations/consultationUtils';
import { Card, StatusChip } from 'shared/components/Feedback';
import { PatternBar } from 'shared/components/PatternBar';
import { ResultContentCard } from 'shared/components/ResultContentCard';
import { formatDateTime, localizedText, omitScoringFields } from 'shared/utilities/format';
import { resolvePatternAppearance } from 'theme/patternColors';

export function ResultPresentation({
  result,
  sponsored = false,
  showConsultationActions = true,
  sponsorCompanyName = null,
  home = null,
}) {
  const { t, locale } = useI18n();
  const payload = omitScoringFields(result || {});
  const resolvedSponsorName = sponsorCompanyName
    || payload.sponsoring_company_name
    || payload.sponsor?.company_name
    || null;
  const hero = payload.hero || {};
  const patterns = Array.isArray(payload.patterns) ? payload.patterns : [];
  const items = Array.isArray(payload.ordered_items) ? payload.ordered_items : [];
  const summary = payload.personal_summary;

  return (
    <div className="result-page">
      {sponsored ? (
        <div className="sponsored-banner" role="note">
          <StatusChip tone="accent">{t('company.participantResult.badge')}</StatusChip>
          {payload.participant_name ? (
            <p className="muted">{t('company.participantResult.participant', { name: payload.participant_name })}</p>
          ) : null}
        </div>
      ) : null}
      {!sponsored && resolvedSponsorName ? (
        <div className="sponsored-banner" role="note">
          <StatusChip tone="accent">{t('results.sponsoredBy', { companyName: resolvedSponsorName })}</StatusChip>
        </div>
      ) : null}
      <section className="result-hero">
        <p className="kicker">{t('results.profileCode')}</p>
        <p className="profile-code" data-testid="profile-code">{payload.profile_code}</p>
        <h1>{localizedText(hero, locale, ['title'])}</h1>
        <p>{localizedText(hero, locale, ['body', 'introduction'])}</p>
        {!sponsored && payload.is_latest ? (
          <StatusChip tone="success">{t('results.latest')}</StatusChip>
        ) : !sponsored ? (
          <StatusChip>{t('results.snapshot')}</StatusChip>
        ) : null}
        <p className="muted">{formatDateTime(payload.completed_at, locale)}</p>
      </section>
      <div className="result-layout">
        <Card>
          <h2>{t('results.patterns')}</h2>
          <div className="pattern-list">
            {patterns.map((pattern, index) => {
              const appearance = resolvePatternAppearance(pattern);
              return (
                <PatternBar
                  key={pattern.version_pattern_id || pattern.id || pattern.short_code}
                  name={localizedText(pattern, locale)}
                  description={localizedText(pattern, locale, ['description'])}
                  percentage={pattern.percentage}
                  color={appearance.color}
                  foreground={appearance.foreground}
                  patternKey={appearance.key}
                  rank={pattern.rank || pattern.position || index + 1}
                  isTop={pattern.is_top}
                  isTied={pattern.is_tied}
                  topLabel={t('results.topPattern')}
                  tiedLabel={t('results.tied')}
                />
              );
            })}
          </div>
        </Card>
        {summary ? (
          <Card>
            <h2>{t('results.personalSummary')}</h2>
            <h3>{localizedText(summary, locale, ['title'])}</h3>
            <p>{localizedText(summary, locale, ['description', 'body'])}</p>
            <div className="chip-row">
              {(summary.tags || []).map((tag) => (
                <StatusChip key={tag.id || tag.slug} tone={tag.style_key || 'accent'}>
                  {localizedText(tag, locale, ['label', 'name'])}
                </StatusChip>
              ))}
            </div>
          </Card>
        ) : <div />}
      </div>
      <div className="ordered-items" data-testid="ordered-items">
        {items.map((item, position) => (
          <ResultContentCard
            key={`${item.type}-${item.id || item.position || position}`}
            item={item}
            locale={locale}
          />
        ))}
      </div>
      {showConsultationActions && !sponsored ? (
        <div className="action-row">
          {isActiveConsultationStatus(home?.consultation?.status) && home?.consultation?.id ? (
            <Link className="btn btn-secondary" to={paths.consultation(home.consultation.id)}>{t('actions.viewConsultation')}</Link>
          ) : canRequestConsultation(home || { latest_result: payload }) ? (
            <Link className="btn btn-secondary" to={paths.consultationNew}>{t('actions.requestConsultation')}</Link>
          ) : null}
          <Link className="text-link" to={paths.history}>{t('actions.viewHistory')}</Link>
        </div>
      ) : null}
    </div>
  );
}

export function LiveRegion({ message }) {
  const [announcement, setAnnouncement] = useState('');

  useEffect(() => {
    if (!message) {
      return;
    }
    setAnnouncement('');
    const timer = window.setTimeout(() => setAnnouncement(message), 20);
    return () => window.clearTimeout(timer);
  }, [message]);

  return (
    <div className="sr-only" aria-live="polite" aria-atomic="true">
      {announcement}
    </div>
  );
}
